// Application Initialization Service
// Handles service initialization, map setup, health checks, and initial data loading

import { mapService } from './mapService.js';
import { dataService } from './dataService.js';
import { riskDataService } from './riskDataService.js';
import { healthCheckService } from './healthCheck.js';
import { searchService } from './searchService.js';
import { apiClientInstance } from './apiClient.js';
import { filterManagerInstance } from './filterManager.js';
import { useDataStore } from '../stores/dataStore.js';
import { useFilterStore } from '../stores/filterStore.js';
import { useLoadingStore } from '../stores/loadingStore.js';
import { useMapStore } from '../stores/mapStore.js';
import { useSearchStore } from '../stores/searchStore.js';
import { urlHandlerInstance } from '../utils/urlHandler.js';

export class AppInitializer {
  constructor() {
    this.services = {};
    this.isInitialized = false;
  }

  async initializeServices() {
    console.log('🔧 Initializing services...');

    // Store service references
    this.services = {
      data: dataService,
      map: mapService,
      risk: riskDataService,
      search: searchService,
      apiClient: apiClientInstance,
      filterManager: filterManagerInstance
    };

    // Export services globally for debugging
    window.ModernServices = {
      dataService,
      mapService,
      riskDataService,
      searchService,
      useDataStore,
      useFilterStore,
      useLoadingStore,
      useMapStore,
      useSearchStore
    };

    // Set up search service callbacks for integration
    this.setupSearchIntegration();

    console.log('✅ Services initialized');
    return this.services;
  }

  setupSearchIntegration() {
    console.log('🔍 Setting up search integration...');

    // Set up search service callbacks for integration with map and filters
    searchService.setCallbacks({
      onSearchExecuted: (query, results) => {
        console.log('🔍 Search executed:', { query, resultCount: results.length });
        // Optionally update UI state or analytics here
      },

      onResultSelected: async (result, options) => {
        console.log('🎯 Search result selected:', result);
        await this.handleSearchResultSelection(result, options);
      },

      onSearchCleared: () => {
        console.log('🧹 Search cleared');
        // Optionally restore previous map state or filters
      }
    });

    console.log('✅ Search integration set up');
  }

  async handleSearchResultSelection(result, options) {
    try {
      const { centerMap, applyFilters, zoomLevel } = options;

      // Handle country result with bbox zoom
      if (result.type === 'country' && centerMap) {
        console.log('🌍 Country selected:', result.title);

        // If bbox is included in the result, use it directly
        if (result.bbox) {
          console.log('📐 Zooming to country bounds:', result.bbox);
          mapService.fitBounds(
            [[result.bbox.minLon, result.bbox.minLat], [result.bbox.maxLon, result.bbox.maxLat]],
            { padding: 50, maxZoom: 6 }
          );
        } else if (result.centroid) {
          // Fallback to centroid zoom if bbox not available
          console.log('📍 Zooming to country centroid:', result.centroid);
          mapService.flyTo([result.centroid.lon, result.centroid.lat], 5);
        } else {
          // Final fallback: try fetching bounds from API
          console.log('🔄 Fetching country bounds from API...');
          const boundsResponse = await this.apiClient.getCountryBounds(result.countryId);

          if (boundsResponse.success && boundsResponse.data?.country?.bounds) {
            const { min_lon, min_lat, max_lon, max_lat } = boundsResponse.data.country.bounds;
            console.log('📐 Zooming to fetched country bounds');
            mapService.fitBounds(
              [[min_lon, min_lat], [max_lon, max_lat]],
              { padding: 50, maxZoom: 6 }
            );
          } else if (boundsResponse.data?.country?.centroid) {
            // Use centroid from API if bounds not available
            const { lon, lat } = boundsResponse.data.country.centroid;
            console.log('📍 Zooming to fetched country centroid');
            mapService.flyTo([lon, lat], 5);
          } else {
            console.warn('⚠️ No country bounds or centroid available for:', result.title);
          }
        }
      }
      // Handle mine and other result types with coordinate-based zoom
      else if (centerMap && result.coordinates) {
        const coordinates = result.coordinates;
        const zoom = zoomLevel || this.getZoomLevelForResultType(result.type);

        console.log('📍 Flying to result coordinates:', coordinates, 'zoom:', zoom, 'for', result.title);
        mapService.flyTo(coordinates, zoom);

        // Highlight the search result if it's a mine
        if (result.type === 'mine') {
          console.log('💎 Highlighting mine:', result.title, 'after map animation');

          // Set selection indicator immediately using search coordinates
          mapService.updateSelectionIndicator(result.id, result.coordinates);

          // Force a fresh data load for the new viewport after the animation completes
          setTimeout(async () => {
            console.log('🔄 Forcing viewport data reload after search zoom...');

            // Get the new viewport bounds after the fly animation
            const viewport = mapService.getViewport();

            // Force reload data for this viewport through the filter manager
            await this.filterManager.syncWithViewport(
              viewport.bounds,
              viewport.zoom,
              (data) => {
                console.log('✅ Viewport data reloaded after search, mine count:', data.features?.length || 0);

                // Update the map with fresh data
                const dataStore = useDataStore.getState();
                dataStore.setMines(data);

                // Now try to highlight the mine from the fresh data
                const foundMine = mapService.highlightMineById(result.id);

                if (foundMine) {
                  console.log('✅ Mine found and highlighted after reload:', result.title);
                } else {
                  console.warn('⚠️ Mine still not found after reload:', result.title, 'ID:', result.id);
                }
              },
              (error) => {
                console.error('❌ Failed to reload viewport data after search:', error);
              }
            );
          }, 1500); // Wait for fly animation to complete
        }
      } else if (centerMap && !result.coordinates && result.type !== 'country') {
        console.warn('⚠️ Cannot fly to result - no coordinates available for:', result.title);
      }

      // Apply relevant filters based on search result
      if (applyFilters) {
        await this.applyFiltersForSearchResult(result);
      }

    } catch (error) {
      console.error('❌ Failed to handle search result selection:', error);
      throw error;
    }
  }

  getZoomLevelForResultType(type) {
    // Determine appropriate zoom level based on result type
    const zoomLevels = {
      mine: 6,       // Medium-regional zoom for individual mines - good balance of context and focus
      commodity: 6,  // Medium zoom for commodity regions
      country: 5,    // Wide zoom for countries
      company: 10    // Medium-close zoom for company locations
    };
    return zoomLevels[type] || 8; // Default zoom
  }

  async applyFiltersForSearchResult(result) {
    try {
      const filterStore = useFilterStore.getState();
      const currentFilters = filterStore.getFilters();

      console.log('🔄 Applying filters for search result type:', result.type);

      switch (result.type) {
        case 'mine':
          // For mine results, don't auto-filter - just show the mine
          console.log('Mine selected - no auto-filtering applied');
          break;

        case 'commodity':
          // Filter by the selected commodity
          const currentCommodities = currentFilters.specificCommodities || [];
          if (!currentCommodities.includes(result.title)) {
            console.log('Setting commodity filter:', result.title);
            filterStore.setFilter('specificCommodities', [result.title]);
          }
          break;

        case 'country':
          // Filter by the selected country
          const currentCountries = currentFilters.countries || [];
          if (!currentCountries.includes(result.title)) {
            console.log('Setting country filter:', result.title);
            filterStore.setFilter('countries', [result.title]);
          }
          break;

        case 'company':
          // For company results, we might filter by company name if we have that capability
          console.log('Company selected - no specific filtering available');
          break;

        default:
          console.log('Unknown result type:', result.type);
      }

    } catch (error) {
      console.error('❌ Failed to apply filters for search result:', error);
    }
  }

  async loadMineForHighlight(result) {
    try {
      console.log('🔍 Loading mine data with expanded status filter for highlighting...');

      // Get current filter state
      const filterStore = useFilterStore.getState();
      const currentFilters = filterStore.getFilters();

      // Get current map bounds for a targeted query
      const bounds = mapService.map.getBounds();
      const searchBounds = {
        west: bounds.getWest(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        north: bounds.getNorth()
      };

      // Create expanded status filter that includes the selected mine's status
      const currentStatuses = currentFilters.mineStatus || ['Active', 'Development'];
      const expandedStatuses = [...new Set([...currentStatuses, result.data.mine_status])];

      console.log('🔄 Expanding status filter from', currentStatuses, 'to', expandedStatuses);

      // Make a targeted viewport request with expanded status filter
      const response = await this.services.apiClient.fetchMinesInViewport({
        bounds: searchBounds,
        zoom: mapService.map.getZoom(),
        filters: {
          ...currentFilters,
          mineStatus: expandedStatuses
        }
      });

      if (response.success && response.data) {
        // Temporarily update the map with this expanded dataset
        const dataStore = useDataStore.getState();
        dataStore.setMines(response.data);

        console.log('✅ Loaded expanded mine data, attempting to highlight mine again...');

        // Try to highlight the mine again with the expanded dataset
        setTimeout(() => {
          const foundMine = mapService.highlightMineById(result.id);
          if (foundMine) {
            console.log('✅ Successfully highlighted mine with expanded data');
          } else {
            console.warn('⚠️ Mine still not found even with expanded status filter');
          }
        }, 500); // Small delay to let the data update

      } else {
        console.error('❌ Failed to load expanded mine data:', response.error);
      }

    } catch (error) {
      console.error('❌ Error loading mine for highlight:', error);
    }
  }

  async initializeMap() {
    console.log('🗺️ Initializing map...');

    const mapContainer = document.getElementById('map');
    if (!mapContainer) {
      throw new Error('Map container not found');
    }

    const { mapConfig } = await import('../config/mapConfig.js');
    await mapService.initialize(mapContainer, {
      style: 'mapbox://styles/mapbox/light-v11',
      center: mapConfig.defaults.center,
      zoom: mapConfig.defaults.zoom,
      projection: 'globe'
    });

    console.log('✅ Map initialized');

    // Initialize FilterStore with current map viewport
    const viewport = mapService.getViewport();
    if (viewport && viewport.bounds) {
      const filterStore = useFilterStore.getState();
      filterStore.setViewport(viewport.bounds, viewport.zoom);
      console.log('✅ Initial viewport set in FilterStore:', viewport);
    }
  }

  async loadInitialData() {
    console.log('📊 Loading initial data...');

    try {
      // Use the dataStore's loadInitialData method which loads countries, commodities, and mines
      const dataStore = useDataStore.getState();
      const result = await dataStore.loadInitialData();

      console.log('✅ DataStore loaded:', {
        mines: result.mines.length,
        countries: result.countries.length,
        commodities: result.commodities.length
      });

      return result;

    } catch (error) {
      console.error('❌ Error loading initial data:', error);
      throw error;
    }
  }

  async performHealthCheck() {
    console.log('🏥 Performing health check...');

    const healthResult = await healthCheckService.initialHealthCheck();
    if (!healthResult.healthy) {
      console.warn('⚠️ Proceeding with initialization despite API health check failure');
      // Optionally start periodic checks to detect when API comes back online
      healthCheckService.startPeriodicCheck();
    }

    return healthResult;
  }

  async loadRiskData() {
    console.log('📊 Loading risk data...');

    await riskDataService.loadRiskData();
    console.log('✅ Risk data loaded');
  }

  initializeUrlHandling() {
    console.log('🔗 Initializing URL handling...');

    urlHandlerInstance.init();
    console.log('✅ URL parameter support initialized');
  }

  getServices() {
    return this.services;
  }

  isServicesInitialized() {
    return this.isInitialized;
  }
}

// Export singleton instance
export const appInitializer = new AppInitializer();